import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import controllerDataConfigApi from '@/api/config/controllerDataConfig'
import { getApiErrorMessage } from '@/helpers/notify'
import { queryKeys } from '@/constant/common'
import useTranslation from '@/hooks/useTranslation'
import useSearchAllIOConfig from '../../hooks/controller/useSearchAllIOConfig'
import useDecoderProtocolMap from '../../hooks/controller/useDecoderProtocolMap'
import {
  fetchSubInterfaces,
  subsQueryKey,
} from '../../hooks/controller/resolveSourceInterfaceIds'
import {
  getIOConfigOptions,
  resolveDecoderProtocol,
} from '@/utils/ioConfigOptions'
import { IoTypeMask, LuProtocol } from '@/types/controller'
import {
  buildControllerUpdatePayload,
  resolveDecoderTypeId,
} from './controllerUpdatePayload'
import { decoderOwnerId } from './decoderOwner'

// The list endpoint is paged; this is the page it is walked in when collecting
// every output decoder.
const FETCH_PAGE_SIZE = 200
// Decoder writes must be serialised: the API returns 500 under concurrent
// writes, the same reason the grid's bulk save runs one row at a time.
const REQUEST_GAP_MS = 120
const RETRY_LIMIT = 2
const RETRY_DELAY_MS = 300

const BRAND = '#004744'
const BRAND_LIGHT = '#0A6B62'

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const fetchAllOutputDecoders = async (interfaceId: number) => {
  const rows: any[] = []
  let pageIndex = 1

  for (;;) {
    const res: any = await controllerDataConfigApi.getAll({
      interfaceId,
      type: IoTypeMask.Output,
      PageIndex: pageIndex,
      PageSize: FETCH_PAGE_SIZE,
    } as any)
    const payload = res?.data
    const list: any[] = payload?.data ?? []
    rows.push(...list)

    const total = Number(payload?.totalCount ?? rows.length)
    if (!list.length || rows.length >= total) break
    pageIndex++
  }

  return rows
}

// A configuration belongs to one line unit protocol, so decoders are grouped by
// theirs: a site that mixes mk2 and mk3 hardware needs a configuration per
// protocol, not one for the whole controller.
type ProtocolKey = 'mk3' | 'mk2' | 'rkd' | 'unknown'

const PROTOCOLS: {
  key: ProtocolKey
  /** The protocol names are hardware identifiers and read the same in every
   *  language; the fallback is a phrase, so it carries a key instead. */
  label?: string
  labelKey?: string
  value?: number
  tone: string
}[] = [
  { key: 'mk3', label: 'mk3', value: LuProtocol.Mk3, tone: BRAND },
  { key: 'mk2', label: 'mk2', value: LuProtocol.Mk2, tone: '#C77700' },
  { key: 'rkd', label: 'rkd', value: LuProtocol.Rkd, tone: '#7A5AF8' },
  {
    key: 'unknown',
    labelKey: 'controllersApplyConfigProtocolUnknown',
    tone: '#8B949E',
  },
]

const protocolKeyOf = (protocol?: number): ProtocolKey => {
  if (protocol === LuProtocol.Mk3) return 'mk3'
  if (protocol === LuProtocol.Mk2) return 'mk2'
  if (protocol === LuProtocol.Rkd) return 'rkd'
  return 'unknown'
}

const secondaryActionSx = {
  height: 38,
  minWidth: 96,
  borderRadius: '10px',
  textTransform: 'none' as const,
  fontSize: '13px',
}

const primaryActionSx = {
  ...secondaryActionSx,
  minWidth: 150,
  color: '#fff',
  background: `linear-gradient(120deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
  boxShadow: '0 6px 16px rgba(0,71,68,0.22)',
  '&:hover': {
    background: `linear-gradient(120deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
    boxShadow: '0 10px 22px rgba(0,71,68,0.30)',
  },
}

interface BulkApplyConfigModalProps {
  open: boolean
  onClose: () => void
  interfaceId: number
}

/**
 * SCRUM-1032 QC item 8 — a failure the user can act on.
 *
 * This used to be the string `address-index`, joined into a one-line strip. On
 * a run where 481 decoders failed that was an unreadable, clipped list naming
 * nothing but numbers. A failure now carries what identifies the decoder on the
 * site — its name, its address, and on a control hub which controller it is on
 * — and, importantly, *why*: the reason used to be caught and discarded, so
 * neither the user nor we could tell whether a run had failed or merely said so.
 */
export interface ApplyFailure {
  /** The decoder's own interface — a sub-controller, on a control hub. */
  interfaceId: number
  address: number
  index: number
  name: string
  controllerName: string
  protocol: string
  reason: string
}

interface GroupProgress {
  total: number
  done: number
  failures: ApplyFailure[]
}

const groupId = (controllerId: number, protocol: ProtocolKey) =>
  `${controllerId}:${protocol}`

/**
 * SCRUM-1032 — bulk apply output configurations.
 *
 * A controller's decoders are not necessarily one generation of hardware, and a
 * configuration only runs on the line unit protocol it was written for. So the
 * decoders are counted per controller and per protocol, and each of those
 * groups picks its own configuration from the ones that actually fit it.
 *
 * The write is the decoder update endpoint, looped one decoder at a time (the
 * API 500s on concurrent writes). The interface-wide `apply-cfg-multiple`
 * endpoint would be one call, but its response carries no total, so it could
 * only ever report "page N" instead of real progress.
 */
const BulkApplyConfigModal = ({
  open,
  onClose,
  interfaceId,
}: BulkApplyConfigModalProps) => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  // The protocol names are hardware identifiers; only the "not detected"
  // fallback is a phrase, so only it carries a key.
  const protocolLabel = useCallback(
    (spec?: { label?: string; labelKey?: string }) =>
      (spec?.labelKey ? t(spec.labelKey) : spec?.label) ?? '',
    [t],
  )
  const { data: ioConfigs } = useSearchAllIOConfig(interfaceId)
  const { data: protocolData } = useDecoderProtocolMap(interfaceId)

  // Shares the `subs` cache entry with useIsControlHub / useSearchAllIOConfig,
  // so opening the modal costs no extra request.
  const { data: subs } = useQuery(
    subsQueryKey(interfaceId),
    () => fetchSubInterfaces(interfaceId),
    { enabled: interfaceId > 0, staleTime: 5 * 60 * 1000, placeholderData: [] },
  )

  const controllers = useMemo(() => {
    const list = ((subs ?? []) as any[])
      .map((sub) => ({
        id: Number(sub.interfaceId),
        name:
          sub.twiCustomName ||
          sub.customName ||
          sub.name ||
          t('controllersApplyConfigControllerFallback', {
            id: sub.interfaceId,
          }),
      }))
      .filter((sub) => sub.id > 0)

    return list.length
      ? list
      : [{ id: interfaceId, name: t('controllersTabOutputDecoders') }]
  }, [subs, interfaceId, t])

  const isControlHub = controllers.length > 1 || controllers[0]?.id !== interfaceId

  const [rows, setRows] = useState<any[] | null>(null)
  const [loadingRows, setLoadingRows] = useState(false)
  const [picks, setPicks] = useState<Record<string, string>>({})
  const [progress, setProgress] = useState<Record<string, GroupProgress>>({})
  const [running, setRunning] = useState(false)
  const [finished, setFinished] = useState(false)
  // SCRUM-1032 QC item 8 — the failure list, opened from the count.
  const [showFailures, setShowFailures] = useState(false)
  // SCRUM-1032 QC item 9 — reading the decoders back to check the failures.
  const [verifying, setVerifying] = useState(false)
  const cancelRef = useRef(false)

  // The decoders are read once when the modal opens: the counts per protocol
  // are the whole point of the screen, so they cannot wait until Apply.
  useEffect(() => {
    if (!open || !interfaceId) return
    let cancelled = false

    setLoadingRows(true)
    fetchAllOutputDecoders(interfaceId)
      .then((fetched) => {
        if (!cancelled) setRows(fetched)
      })
      .catch((error: any) => {
        if (cancelled) return
        setRows([])
        toast.error(
          error?.response?.data?.Message ||
            t('controllersApplyConfigLoadFailed'),
        )
      })
      .finally(() => {
        if (!cancelled) setLoadingRows(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, interfaceId, t])

  const configsFor = useCallback(
    (controllerId: number) =>
      ioConfigs?.filter((cfg) => Number(cfg.interfaceId) === controllerId) ?? [],
    [ioConfigs],
  )

  // Decoders bucketed by controller, then by the protocol their line unit (or
  // current configuration) reports.
  const groupsByController = useMemo(() => {
    const map = new Map<number, Map<ProtocolKey, any[]>>()
    controllers.forEach((controller) => map.set(controller.id, new Map()))

    ;(rows ?? []).forEach((row) => {
      const controllerId = isControlHub
        ? decoderOwnerId(row, interfaceId)
        : controllers[0].id
      const buckets = map.get(controllerId)
      if (!buckets) return

      const protocol = resolveDecoderProtocol(
        configsFor(controllerId),
        protocolData?.protocolByAddress.get(Number(row?.address)),
        Number(row?.configurationId),
      )
      const key = protocolKeyOf(protocol)
      buckets.set(key, [...(buckets.get(key) ?? []), row])
    })

    return map
  }, [rows, controllers, isControlHub, configsFor, protocolData, interfaceId])

  const optionsFor = useCallback(
    (controllerId: number, protocol: ProtocolKey) => {
      const spec = PROTOCOLS.find((p) => p.key === protocol)
      return getIOConfigOptions(
        configsFor(controllerId),
        IoTypeMask.Output,
        spec?.value,
      )
    },
    [configsFor],
  )

  const selectedGroups = useMemo(() => {
    const chosen: {
      controllerId: number
      protocol: ProtocolKey
      option: { id: number; label: string }
      rows: any[]
    }[] = []

    controllers.forEach((controller) => {
      const buckets = groupsByController.get(controller.id)
      if (!buckets) return
      buckets.forEach((bucketRows, protocol) => {
        const label = picks[groupId(controller.id, protocol)]
        if (!label) return
        const option = optionsFor(controller.id, protocol).find(
          (opt) => opt.label === label,
        )
        if (!option) return
        chosen.push({
          controllerId: controller.id,
          protocol,
          option,
          rows: bucketRows,
        })
      })
    })

    return chosen
  }, [controllers, groupsByController, picks, optionsFor])

  const totals = useMemo(
    () =>
      Object.values(progress).reduce(
        (acc, group) => ({
          total: acc.total + group.total,
          done: acc.done + group.done,
          failed: acc.failed + group.failures.length,
        }),
        { total: 0, done: 0, failed: 0 },
      ),
    [progress],
  )

  /** Every failure across every group, for the list the count opens. */
  const allFailures = useMemo(
    () => Object.values(progress).flatMap((group) => group.failures),
    [progress],
  )

  /**
   * The whole list as text. On a run where hundreds failed, the useful next
   * step is pasting them into a ticket, not reading them off the screen.
   */
  const copyFailures = useCallback(() => {
    const text = allFailures
      .map((f) =>
        [
          `${f.address}-${f.index}`,
          f.name,
          f.controllerName,
          f.protocol,
          f.reason,
        ]
          .filter(Boolean)
          .join('\t'),
      )
      .join('\n')
    navigator.clipboard
      ?.writeText(text)
      .then(() => toast.success(t('controllersApplyConfigFailuresCopied')))
      .catch(() => undefined)
  }, [allFailures, t])

  const handleApply = useCallback(async () => {
    if (!selectedGroups.length || loadingRows) return

    cancelRef.current = false
    setRunning(true)
    setFinished(false)
    setProgress(
      Object.fromEntries(
        selectedGroups.map((group) => [
          groupId(group.controllerId, group.protocol),
          { total: group.rows.length, done: 0, failures: [] },
        ]),
      ),
    )

    let appliedAll = 0
    let failedAll = 0
    /** Each group's failures, for the read-back below to reconsider. */
    const groupFailures = new Map<string, ApplyFailure[]>()

    for (const group of selectedGroups) {
      if (cancelRef.current) break
      const key = groupId(group.controllerId, group.protocol)

      let done = 0
      const failures: ApplyFailure[] = []
      const controllerName =
        controllers.find((c) => c.id === group.controllerId)?.name ?? ''
      const protocolName = protocolLabel(
        PROTOCOLS.find((p) => p.key === group.protocol),
      )

      for (const row of group.rows) {
        if (cancelRef.current) break

        let saved = false
        // Kept so a failure can say why. Previously swallowed, which is why a
        // run that failed on every decoder left nothing to diagnose it with.
        let lastError: unknown
        for (let attempt = 0; attempt < RETRY_LIMIT && !saved; attempt++) {
          try {
            await controllerDataConfigApi.update(
              buildControllerUpdatePayload(row, interfaceId, {
                configuration: Number(group.option.id),
                decoderType: resolveDecoderTypeId(row.decoderTypeId),
              }) as any,
            )
            saved = true
          } catch (error) {
            lastError = error
            if (attempt < RETRY_LIMIT - 1) await sleep(RETRY_DELAY_MS)
          }
        }

        if (saved) done++
        else
          failures.push({
            interfaceId: decoderOwnerId(row, interfaceId),
            address: Number(row.address),
            index: Number(row.index),
            name: String(row.name ?? `${row.address}-${row.index}`),
            controllerName,
            protocol: protocolName,
            reason: getApiErrorMessage(
              lastError,
              t('controllersApplyConfigFailureUnknownReason'),
            ),
          })

        const snapshot: GroupProgress = {
          total: group.rows.length,
          done,
          failures: [...failures],
        }
        setProgress((prev) => ({ ...prev, [key]: snapshot }))
        await sleep(REQUEST_GAP_MS)
      }

      appliedAll += done
      failedAll += failures.length
      groupFailures.set(key, failures)
    }

    /**
     * SCRUM-1032 QC item 9 — believe the controller, not the exception.
     *
     * Wei Woon reported decoders listed as failed that, searched for
     * afterwards, plainly carried the configuration. The write endpoint is
     * known to answer 500 under concurrent writes (it is why this loop is
     * serialised at all), and a 500 raised after the row has been committed is
     * indistinguishable here from one raised instead of committing.
     *
     * So a failure is only reported once the decoder has been read back and
     * still disagrees. Only run when something failed, and only one extra pass
     * over the list however many failed.
     */
    if (failedAll > 0 && !cancelRef.current) {
      setVerifying(true)
      try {
        const fresh = await fetchAllOutputDecoders(interfaceId)
        const configById = new Map<string, number>()
        fresh.forEach((row: any) => {
          const owner = decoderOwnerId(row, interfaceId)
          configById.set(
            `${owner}-${row.address}-${row.index}`,
            Number(row.configurationId),
          )
        })

        selectedGroups.forEach((group) => {
          const key = groupId(group.controllerId, group.protocol)
          const failures = groupFailures.get(key) ?? []
          if (!failures.length) return

          const stillFailed = failures.filter((failure) => {
            const applied = configById.get(
              `${failure.interfaceId}-${failure.address}-${failure.index}`,
            )
            return applied !== Number(group.option.id)
          })

          const recovered = failures.length - stillFailed.length
          if (!recovered) return

          appliedAll += recovered
          failedAll -= recovered
          setProgress((prev) => ({
            ...prev,
            [key]: {
              total: prev[key]?.total ?? group.rows.length,
              done: (prev[key]?.done ?? 0) + recovered,
              failures: stillFailed,
            },
          }))
        })
      } catch {
        // The read-back is a second opinion, not the run. If it cannot be had,
        // the failures stand as the writes reported them.
      } finally {
        setVerifying(false)
      }
    }

    setRunning(false)
    setFinished(true)
    queryClient.invalidateQueries([queryKeys.controller])

    if (cancelRef.current) {
      toast(
        t(
          appliedAll === 1
            ? 'controllersApplyConfigStoppedOne'
            : 'controllersApplyConfigStoppedOther',
          { count: appliedAll },
        ),
      )
    } else if (failedAll) {
      toast.error(
        t('controllersApplyConfigFailedCount', { count: failedAll }),
      )
    } else {
      toast.success(
        t('controllersApplyConfigApplied', { count: appliedAll }),
      )
    }
  }, [selectedGroups, loadingRows, interfaceId, queryClient, t, controllers, protocolLabel])

  const handleClose = () => {
    if (running) return
    // Dropped rather than kept for the next opening, which reads them again.
    setRows(null)
    setPicks({})
    setProgress({})
    setFinished(false)
    onClose()
  }

  const decoderCountLabel = useCallback(
    (count: number) =>
      t(
        count === 1
          ? 'controllersApplyConfigDecoderCountOne'
          : 'controllersApplyConfigDecoderCountOther',
        { count },
      ),
    [t],
  )

  const decoderCount = rows?.length ?? 0
  const selectedCount = selectedGroups.reduce(
    (sum, group) => sum + group.rows.length,
    0,
  )
  const overallPercent = totals.total
    ? ((totals.done + totals.failed) / totals.total) * 100
    : 0

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={false}>
      <Stack
        sx={{
          width: 660,
          maxWidth: '100%',
          backgroundColor: 'background.default',
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          sx={{
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '16px',
            p: '24px 28px 18px',
          }}
        >
          <Stack direction="row" sx={{ gap: '14px', alignItems: 'flex-start' }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                flexShrink: 0,
                borderRadius: '11px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                background: `linear-gradient(140deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
                boxShadow: '0 8px 18px rgba(0,71,68,0.24)',
              }}
            >
              <PlaylistAddCheckRoundedIcon sx={{ fontSize: 20 }} />
            </Box>
            <Stack sx={{ gap: '4px' }}>
            <Typography
              sx={{
                color: 'text.main',
                fontSize: '19px',
                fontWeight: 600,
                letterSpacing: '-0.01em',
              }}
            >
              {t('commonActionApplyConfiguration')}
            </Typography>
            <Typography sx={{ color: 'grey.800', fontSize: '13px' }}>
              {loadingRows
                ? t('controllersApplyConfigReading')
                : t('controllersApplyConfigSubtitle')}
            </Typography>
            </Stack>
          </Stack>
          {!loadingRows && (
            <Stack
              sx={{
                alignItems: 'flex-end',
                flexShrink: 0,
                pt: '2px',
              }}
            >
              <Typography
                sx={{ color: 'text.main', fontSize: '20px', fontWeight: 700 }}
              >
                {decoderCount}
              </Typography>
              <Typography
                sx={{
                  color: 'grey.800',
                  fontSize: '10px',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                {t('controllersTabOutputDecoders')}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Divider sx={{ borderColor: 'border.main' }} />

        {/* One block per controller; one row per protocol found on it. */}
        <Stack
          sx={{
            p: '18px 28px',
            gap: '18px',
            maxHeight: '52vh',
            overflowY: 'auto',
          }}
        >
          {loadingRows && (
            <Stack sx={{ alignItems: 'center', py: '32px' }}>
              <CircularProgress size={22} />
            </Stack>
          )}

          {!loadingRows &&
            controllers.map((controller) => {
              const buckets = groupsByController.get(controller.id)
              const present = PROTOCOLS.filter(
                (spec) => (buckets?.get(spec.key)?.length ?? 0) > 0,
              )
              const controllerTotal = present.reduce(
                (sum, spec) => sum + (buckets?.get(spec.key)?.length ?? 0),
                0,
              )

              return (
                <Stack key={controller.id} sx={{ gap: '8px' }}>
                  <Stack sx={{ gap: '6px' }}>
                    <Stack
                      direction="row"
                      sx={{ alignItems: 'baseline', gap: '10px' }}
                    >
                      <Typography
                        sx={{
                          color: 'text.main',
                          fontSize: '13px',
                          fontWeight: 700,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {controller.name}
                      </Typography>
                      <Typography sx={{ color: 'grey.800', fontSize: '12px' }}>
                        {present.length > 1
                          ? t('controllersApplyConfigMixedProtocols', {
                              decoders: decoderCountLabel(controllerTotal),
                            })
                          : decoderCountLabel(controllerTotal)}
                      </Typography>
                    </Stack>

                  </Stack>

                  {present.length === 0 ? (
                    <Typography
                      sx={{
                        color: 'grey.800',
                        fontSize: '12px',
                        p: '10px 14px',
                        borderRadius: '10px',
                        border: '1px dashed',
                        borderColor: 'border.main',
                      }}
                    >
                      {t('controllersApplyConfigNoDecoders')}
                    </Typography>
                  ) : (
                    <Stack
                      sx={{
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: 'border.main',
                        overflow: 'hidden',
                      }}
                    >
                      {present.map((spec, index) => {
                        const bucketRows = buckets?.get(spec.key) ?? []
                        const key = groupId(controller.id, spec.key)
                        const options = optionsFor(controller.id, spec.key)
                        const pick = picks[key] ?? ''
                        const groupProgress = progress[key]
                        const failed = groupProgress?.failures.length ?? 0
                        const percent = groupProgress?.total
                          ? ((groupProgress.done + failed) /
                              groupProgress.total) *
                            100
                          : 0
                        const complete =
                          finished && !!groupProgress && failed === 0

                        return (
                          <Stack
                            key={key}
                            sx={{
                              borderTop: index > 0 ? '1px solid' : 'none',
                              borderColor: 'border.main',
                            }}
                          >
                            <Stack
                              direction="row"
                              sx={{
                                alignItems: 'center',
                                gap: '12px',
                                p: '10px 14px',
                                transition: 'background-color 140ms ease',
                                backgroundColor: pick
                                  ? `${spec.tone}0F`
                                  : 'transparent',
                                '&:hover': {
                                  backgroundColor: pick
                                    ? `${spec.tone}17`
                                    : 'background.overrideDefault',
                                },
                              }}
                            >
                              {/* Protocol + how many decoders are on it: the
                                  count is what tells the user a site is mixed. */}
                              <Stack
                                direction="row"
                                sx={{
                                  alignItems: 'center',
                                  gap: '7px',
                                  width: 104,
                                  flexShrink: 0,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: spec.tone,
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  sx={{
                                    color: 'text.main',
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    fontVariantNumeric: 'tabular-nums',
                                    minWidth: 26,
                                    textAlign: 'right',
                                  }}
                                >
                                  {bucketRows.length}
                                </Typography>
                                <Typography
                                  sx={{
                                    color: 'grey.800',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {protocolLabel(spec)}
                                </Typography>
                              </Stack>

                              <TextField
                                select
                                size="small"
                                value={pick}
                                disabled={running || options.length === 0}
                                onChange={(event) =>
                                  setPicks((prev) => ({
                                    ...prev,
                                    [key]: event.target.value,
                                  }))
                                }
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  '& .MuiInputBase-root': {
                                    height: 38,
                                    fontSize: '13px',
                                    borderRadius: '9px',
                                    color: 'text.main',
                                  },
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'border.main',
                                  },
                                  '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'primary.main',
                                  },
                                }}
                                SelectProps={{
                                  displayEmpty: true,
                                  renderValue: (value: any) =>
                                    value ? (
                                      String(value)
                                    ) : (
                                      <Box
                                        component="span"
                                        sx={{ color: 'grey.600' }}
                                      >
                                        {t(
                                          'controllersApplyConfigLeaveUnchanged',
                                        )}
                                      </Box>
                                    ),
                                }}
                              >
                                <MenuItem value="" sx={{ fontSize: '13px' }}>
                                  <em>
                                    {t('controllersApplyConfigLeaveUnchanged')}
                                  </em>
                                </MenuItem>
                                {options.length === 0 && (
                                  <MenuItem
                                    value=""
                                    disabled
                                    sx={{ fontSize: '13px' }}
                                  >
                                    {t(
                                      'controllersApplyConfigNoConfigsForProtocol',
                                      { protocol: protocolLabel(spec) },
                                    )}
                                  </MenuItem>
                                )}
                                {options.map((option) => (
                                  <MenuItem
                                    key={`${option.id}-${option.label}`}
                                    value={option.label}
                                    sx={{ fontSize: '13px' }}
                                  >
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </TextField>

                              <Stack
                                direction="row"
                                sx={{
                                  alignItems: 'center',
                                  gap: '6px',
                                  width: 74,
                                  justifyContent: 'flex-end',
                                  flexShrink: 0,
                                }}
                              >
                                {complete && (
                                  <CheckRoundedIcon
                                    sx={{ fontSize: 16, color: 'primary.main' }}
                                  />
                                )}
                                {failed > 0 && (
                                  <ErrorOutlineIcon
                                    sx={{ fontSize: 16, color: '#dc3545' }}
                                  />
                                )}
                                {groupProgress && (
                                  <Typography
                                    sx={{
                                      color: 'grey.800',
                                      fontSize: '11px',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {groupProgress.done + failed}/
                                    {groupProgress.total}
                                  </Typography>
                                )}
                              </Stack>
                            </Stack>

                            {groupProgress && (
                              <LinearProgress
                                variant="determinate"
                                value={percent}
                                sx={{
                                  height: 3,
                                  backgroundColor: 'transparent',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: spec.tone,
                                  },
                                }}
                              />
                            )}

                            {/* SCRUM-1032 QC item 8 — the count opens the
                                list. It used to be every address joined into a
                                clipped 44px strip, which on a run of 481
                                failures named nothing anyone could act on. */}
                            {failed > 0 && (
                              <Stack
                                sx={{ p: '0 14px 10px', alignItems: 'flex-start' }}
                              >
                                <Typography
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => setShowFailures(true)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault()
                                      setShowFailures(true)
                                    }
                                  }}
                                  sx={{
                                    color: '#dc3545',
                                    fontSize: '11.5px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    textUnderlineOffset: '2px',
                                    '&:hover': { opacity: 0.8 },
                                  }}
                                >
                                  {t(
                                    failed === 1
                                      ? 'controllersApplyConfigFailedCountOne'
                                      : 'controllersApplyConfigFailedCountOther',
                                    { count: failed },
                                  )}
                                </Typography>
                              </Stack>
                            )}
                          </Stack>
                        )
                      })}
                    </Stack>
                  )}
                </Stack>
              )
            })}
        </Stack>

        {/* Overall progress + actions */}
        <Divider sx={{ borderColor: 'border.main' }} />
        <Stack sx={{ p: '14px 28px 20px', gap: '12px' }}>
          {(running || finished) && (
            <Stack sx={{ gap: '6px' }}>
              <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
                <Typography sx={{ color: 'text.main', fontSize: '12px' }}>
                  {running
                    ? t('controllersApplyConfigRunning')
                    : verifying
                      ? t('controllersApplyConfigVerifying')
                      : t('controllersApplyConfigFinished')}
                </Typography>
                <Typography sx={{ color: 'grey.800', fontSize: '12px' }}>
                  {totals.done + totals.failed} / {totals.total}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={overallPercent}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'border.main',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 3,
                    backgroundColor: 'primary.main',
                  },
                }}
              />
            </Stack>
          )}

          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
            }}
          >
            <Stack direction="row" sx={{ alignItems: 'center', gap: '8px' }}>
              {selectedGroups.length ? (
                selectedGroups.map((group) => {
                  const spec = PROTOCOLS.find((p) => p.key === group.protocol)
                  return (
                    <Stack
                      key={`${group.controllerId}-${group.protocol}`}
                      direction="row"
                      sx={{
                        alignItems: 'center',
                        gap: '5px',
                        height: 22,
                        px: '8px',
                        borderRadius: '11px',
                        backgroundColor: `${spec?.tone ?? BRAND}1A`,
                      }}
                    >
                      <Typography
                        sx={{
                          color: spec?.tone ?? BRAND,
                          fontSize: '11px',
                          fontWeight: 700,
                        }}
                      >
                        {group.rows.length}
                      </Typography>
                      <Typography
                        sx={{ color: spec?.tone ?? BRAND, fontSize: '11px' }}
                      >
                        {protocolLabel(spec)}
                      </Typography>
                    </Stack>
                  )
                })
              ) : (
                <Typography sx={{ color: 'grey.800', fontSize: '12px' }}>
                  {t('controllersApplyConfigNothingSelected')}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={1}>
              {running ? (
                <Button
                  variant="secondary"
                  sx={secondaryActionSx}
                  onClick={() => {
                    cancelRef.current = true
                  }}
                >
                  {t('commonLabelsStop')}
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  sx={secondaryActionSx}
                  onClick={handleClose}
                >
                  {finished ? t('commonActionsClose') : t('commonActionsCancel')}
                </Button>
              )}
              <Button
                variant="primary"
                sx={primaryActionSx}
                // Not while the decoders are being read: the rows on hand until
                // then are the previous opening's, and writing those puts back
                // whatever changed since (QC round 2, item 3).
                disabled={
                  !finished && (!selectedGroups.length || running || loadingRows)
                }
                onClick={finished ? handleClose : handleApply}
                data-testid="apply-config-submit"
                startIcon={
                  running ? (
                    <CircularProgress size={14} sx={{ color: '#fff' }} />
                  ) : undefined
                }
              >
                {/* SCRUM-1032 QC, 5 Oct — the way out of a finished run.
                    The bar read "Finished 481 / 481" while this button still
                    said "Apply to 481 decoders", so the only thing left to do
                    looked like doing it again. Once the run is done this is
                    Done, and it closes. */}
                {finished
                  ? t('commonActionsDone')
                  : selectedCount
                    ? t(
                        selectedCount === 1
                          ? 'controllersApplyConfigApplyToOne'
                          : 'controllersApplyConfigApplyToOther',
                        { count: selectedCount },
                      )
                    : t('commonActionsApply')}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      {/* SCRUM-1032 QC item 8 — which decoders failed, and why.
          A sibling dialog rather than a nested one: this opens from inside an
          open Dialog, and MUI leaves a modal inside a modal unreachable. */}
      <Dialog
        open={showFailures}
        onClose={() => setShowFailures(false)}
        maxWidth={false}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        {/* Width on the content, matching the dialog above it: this MUI
            version's Dialog does not expose `paper` through slotProps. */}
        <Stack
          sx={{
            width: 720,
            maxWidth: '100%',
            backgroundColor: 'background.default',
            minHeight: 0,
          }}
        >
          {/* Header */}
          <Stack
            direction="row"
            sx={{
              alignItems: 'flex-start',
              gap: '14px',
              p: '22px 26px 16px',
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                flexShrink: 0,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                backgroundColor: '#dc3545',
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 20 }} />
            </Box>
            <Stack sx={{ gap: '3px', flex: 1, minWidth: 0 }}>
              <Typography
                sx={{ color: 'text.main', fontSize: '18px', fontWeight: 600 }}
              >
                {t(
                  allFailures.length === 1
                    ? 'controllersApplyConfigFailuresTitleOne'
                    : 'controllersApplyConfigFailuresTitleOther',
                  { count: allFailures.length },
                )}
              </Typography>
              <Typography sx={{ color: 'grey.800', fontSize: '13px' }}>
                {t('controllersApplyConfigFailuresSubtitle')}
              </Typography>
            </Stack>
          </Stack>
          <Divider sx={{ borderColor: 'border.main' }} />

          {/* Column headings, so the reason column is not mistaken for prose. */}
          <Stack
            direction="row"
            sx={{
              gap: '12px',
              px: '26px',
              py: '8px',
              backgroundColor: 'background.overrideDefault',
            }}
          >
            {[
              { key: 'controllersApplyConfigFailuresColDecoder', width: 210 },
              { key: 'controllersApplyConfigFailuresColAddress', width: 116 },
              { key: 'controllersApplyConfigFailuresColReason', flex: 1 },
            ].map((col) => (
              <Typography
                key={col.key}
                sx={{
                  width: col.width,
                  flex: col.flex,
                  minWidth: 0,
                  color: 'grey.800',
                  fontSize: '10.5px',
                  fontWeight: 700,
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                }}
              >
                {t(col.key)}
              </Typography>
            ))}
          </Stack>
          <Divider sx={{ borderColor: 'border.main' }} />

          <Stack sx={{ maxHeight: '52vh', overflowY: 'auto' }}>
            {allFailures.map((failure, index) => (
              <Stack
                key={`${failure.address}-${failure.index}-${index}`}
                direction="row"
                sx={{
                  gap: '12px',
                  px: '26px',
                  py: '9px',
                  alignItems: 'baseline',
                  borderTop: index > 0 ? '1px solid' : 'none',
                  borderColor: 'border.main',
                }}
              >
                <Stack sx={{ width: 210, minWidth: 0, gap: '1px' }}>
                  <Typography
                    sx={{
                      color: 'text.main',
                      fontSize: '13px',
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {failure.name}
                  </Typography>
                  {/* Only worth a line when there is more than one controller
                      to tell apart. */}
                  {isControlHub && failure.controllerName && (
                    <Typography
                      sx={{
                        color: 'grey.600',
                        fontSize: '11px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {failure.controllerName}
                    </Typography>
                  )}
                </Stack>
                <Stack sx={{ width: 116, minWidth: 0, gap: '1px' }}>
                  <Typography
                    sx={{
                      color: 'text.main',
                      fontSize: '12.5px',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {failure.address}-{failure.index}
                  </Typography>
                  {failure.protocol && (
                    <Typography sx={{ color: 'grey.600', fontSize: '11px' }}>
                      {failure.protocol}
                    </Typography>
                  )}
                </Stack>
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    color: 'grey.800',
                    fontSize: '12.5px',
                    lineHeight: 1.35,
                  }}
                >
                  {failure.reason}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <Divider sx={{ borderColor: 'border.main' }} />
          <Stack
            direction="row"
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              p: '14px 26px 18px',
            }}
          >
            <Button
              variant="secondary"
              sx={secondaryActionSx}
              onClick={copyFailures}
            >
              {t('controllersApplyConfigFailuresCopy')}
            </Button>
            <Button
              variant="primary"
              sx={primaryActionSx}
              onClick={() => setShowFailures(false)}
            >
              {t('commonActionsClose')}
            </Button>
          </Stack>
        </Stack>
      </Dialog>
    </Dialog>
  )
}

export default BulkApplyConfigModal
